// frontend/src/components/WareHouses.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { 
  Warehouse, Plus, Building, AlertCircle, ChevronDown, 
  Loader2, X, Save, MapPin, Hash
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./WareHouses.css";

const url = process.env.REACT_APP_BACKEND_URL;

const WareHouses = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  
  // For system admins: company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Track next warehouse ID per company
  const [nextWarehouseId, setNextWarehouseId] = useState(1);
  
  // Form data
  const [formData, setFormData] = useState({
    warehouse_code: "", // Will be auto-generated
    warehouse_name: "",
    location: "",
    company_id: ""
  });

  // Calculate user permissions
  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };
  
  const isCompanyAdminUser = () => {
    return userType === 'company' && currentUser?.role === 'admin';
  };

  const userCanCreateWarehouse = () => {
    return isCompanyAdminUser() || isSystemAdminUser();
  };

  // Get next warehouse number for a company
  const getNextWarehouseId = async (companyId) => {
    try {
      if (!companyId) {
        return 1;
      }
      
      // Fetch existing warehouses for this company
      const response = await axios.get(`${url}/api/warehouse`, {
        params: { company_id: companyId }
      });
      
      if (response.data.success) {
        const warehouses = response.data.data || [];
        
        if (warehouses.length === 0) {
          return 1; // First warehouse for this company
        }
        
        // Find the highest warehouse_id
        let highestId = 0;
        warehouses.forEach(warehouse => {
          if (warehouse.warehouse_id > highestId) {
            highestId = warehouse.warehouse_id;
          }
        });
        
        return highestId + 1;
      }
      
      return 1;
    } catch (error) {
      return 1; // Fallback
    }
  };

  // Generate warehouse code based on sequential ID
  const generateWarehouseCode = (warehouseId) => {
    return `WH${warehouseId.toString().padStart(3, '0')}`;
  };

  // Fetch companies for system admins
  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Check permissions and setup initial data
  useEffect(() => {
    const checkPermissionsAndSetup = async () => {
      try {
        if (!userCanCreateWarehouse()) {
          toast.error("You don't have permission to create warehouses");
          navigate("/");
          return;
        }
        
        if (isSystemAdminUser()) {
          await fetchCompanies();
        } else if (userType === 'company' && company) {
          const companyId = company._id || company;
          setFormData(prev => ({
            ...prev,
            company_id: companyId
          }));
          
          // Get next warehouse ID and generate code
          const nextId = await getNextWarehouseId(companyId);
          setNextWarehouseId(nextId);
          const warehouseCode = generateWarehouseCode(nextId);
          setFormData(prev => ({ ...prev, warehouse_code: warehouseCode }));
        }
        
        setLoading(false);
      } catch (error) {
        toast.error("Failed to load permissions");
        navigate("/");
      }
    };
    
    checkPermissionsAndSetup();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleCompanyChange = async (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    setFormData(prev => ({ ...prev, company_id: value }));
    
    if (value) {
      // Get next warehouse ID when company is selected
      const nextId = await getNextWarehouseId(value);
      setNextWarehouseId(nextId);
      const warehouseCode = generateWarehouseCode(nextId);
      setFormData(prev => ({ ...prev, warehouse_code: warehouseCode }));
    } else {
      setNextWarehouseId(1);
      setFormData(prev => ({ ...prev, warehouse_code: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) =>
    setFocused({ ...focused, [field]: formData[field] && formData[field].length > 0 });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.warehouse_code.trim()) {
      newErrors.warehouse_code = "Warehouse code is required";
    }
    
    if (!formData.warehouse_name.trim()) {
      newErrors.warehouse_name = "Warehouse name is required";
    } else if (formData.warehouse_name.length > 100) {
      newErrors.warehouse_name = "Warehouse name cannot exceed 100 characters";
    }
    
    if (formData.location && formData.location.length > 200) {
      newErrors.location = "Location cannot exceed 200 characters";
    }
    
    if (!formData.company_id) {
      newErrors.company_id = "Company selection is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Prepare payload - include warehouse_id if we know it
      const payload = {
        warehouse_code: formData.warehouse_code,
        warehouse_name: formData.warehouse_name.trim(),
        location: formData.location?.trim() || "",
        warehouse_id: nextWarehouseId // Send the sequential ID explicitly
      };
      
      // Add company_id
      if (isSystemAdminUser() && formData.company_id) {
        payload.company_id = formData.company_id;
      } else if (formData.company_id) {
        payload.company_id = formData.company_id;
      }
      
      const response = await axios.post(`${url}/api/warehouse`, payload);
      
      if (response.data.success) {
        const warehouse = response.data.data;
        toast.success(`Warehouse ${warehouse.warehouse_id}: ${warehouse.warehouse_name} created successfully!`);
        
        // Reset form and get next ID
        if (formData.company_id) {
          const nextId = await getNextWarehouseId(formData.company_id);
          setNextWarehouseId(nextId);
          const warehouseCode = generateWarehouseCode(nextId);
          
          setFormData({
            warehouse_code: warehouseCode,
            warehouse_name: "",
            location: "",
            company_id: formData.company_id
          });
        }
        
        // Navigate to list after delay
        setTimeout(() => {
          navigate("/ViewWareHouses");
        }, 2000);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        // Duplicate warehouse_id error - get the next available ID
        toast.error("Warehouse ID already exists. Getting next available ID...");
        if (formData.company_id) {
          const nextId = await getNextWarehouseId(formData.company_id);
          setNextWarehouseId(nextId);
          const warehouseCode = generateWarehouseCode(nextId);
          setFormData(prev => ({ 
            ...prev, 
            warehouse_code: warehouseCode,
            warehouse_id: nextId 
          }));
          toast.info(`Try again with ID: ${nextId}`);
        }
      } else {
        const errorMsg = error.response?.data?.message || "Failed to create warehouse";
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewWareHouses");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="warehouses-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="warehouses-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <Warehouse size={24} />
          <h2 className="form-title">Add New Warehouse</h2>
          <p className="form-subtitle">
            {isSystemAdminUser() 
              ? "Create a warehouse for any company"
              : `Create a warehouse for ${company?.company_name || 'your company'}`
            }
          </p>
        </div>

        <div className="role-indicator" style={{
          background: isSystemAdminUser() 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #f89a3a 0%, #f55a42 100%)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Building size={20} />
          <div>
            <strong>
              {isSystemAdminUser() ? "System Administrator" : "Company Admin"}
            </strong>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {isSystemAdminUser() 
                ? "You can create warehouses for any company"
                : `You can only create warehouses for ${company?.company_name || 'your company'}`
              }
            </div>
          </div>
        </div>

        <form className="warehouses-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {isSystemAdminUser() && (
              <div className={`form-field required ${focused.company_id || formData.company_id ? "filled" : ""} ${errors.company_id ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="company_id"
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("company_id")}
                    onBlur={() => handleBlur("company_id")}
                    required
                    disabled={loadingCompanies}
                  >
                    <option value="">Select Company</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
                <label>Company</label>
                {errors.company_id && <small className="field-error">{errors.company_id}</small>}
                {loadingCompanies && <small className="field-hint">Loading companies...</small>}
              </div>
            )}

            {/* Warehouse ID and Code display */}
            <div className="warehouse-sequence-section">
              <div className="sequence-card" style={{ 
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                borderLeft: '4px solid #2196f3',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Hash size={20} style={{ color: '#2196f3' }} />
                    <h3 style={{ margin: 0, color: '#1565c0' }}>Warehouse Sequence</h3>
                  </div>
                  <div style={{ 
                    background: '#2196f3', 
                    color: 'white',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Auto-generated
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="sequence-item">
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#666',
                      marginBottom: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <Hash size={12} />
                      Warehouse ID
                    </div>
                    <div style={{
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: '#0d47a1',
                      textAlign: 'center'
                    }}>
                      {nextWarehouseId}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      textAlign: 'center',
                      marginTop: '5px'
                    }}>
                      Sequential number for this company
                    </div>
                  </div>
                  
                  <div className="sequence-item">
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#666',
                      marginBottom: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <Warehouse size={12} />
                      Warehouse Code
                    </div>
                    <div style={{
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: '#1b5e20',
                      textAlign: 'center'
                    }}>
                      {formData.warehouse_code || "WH001"}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      textAlign: 'center',
                      marginTop: '5px'
                    }}>
                      {formData.warehouse_code ? `Based on ID: ${nextWarehouseId}` : 'Will be generated'}
                    </div>
                  </div>
                </div>
                
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  background: '#f5f5f5',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <AlertCircle size={12} />
                    This will be the {nextWarehouseId === 1 ? 'first' : `${nextWarehouseId}th`} warehouse for {
                      selectedCompany 
                        ? companies.find(c => c._id === selectedCompany)?.company_name || 'selected company'
                        : company?.company_name || 'this company'
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Hidden warehouse code field */}
            <input
              type="hidden"
              name="warehouse_code"
              value={formData.warehouse_code}
            />

            <div className={`form-field required ${focused.warehouse_name || formData.warehouse_name ? "filled" : ""} ${errors.warehouse_name ? 'error' : ''}`}>
              <input
                type="text"
                name="warehouse_name"
                value={formData.warehouse_name}
                onChange={handleChange}
                onFocus={() => handleFocus("warehouse_name")}
                onBlur={() => handleBlur("warehouse_name")}
                placeholder="Enter warehouse name (e.g., Main Warehouse, Storage Facility)"
                required
                maxLength={100}
              />
              <label>Warehouse Name</label>
              {errors.warehouse_name && <small className="field-error">{errors.warehouse_name}</small>}
              <small className="field-hint">
                Descriptive name for the warehouse (max 100 characters)
              </small>
            </div>

            <div className={`form-field ${focused.location || formData.location ? "filled" : ""} ${errors.location ? 'error' : ''}`}>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                onFocus={() => handleFocus("location")}
                onBlur={() => handleBlur("location")}
                placeholder="Enter location/address (optional)"
                maxLength={200}
              />
              <label>
                <MapPin size={14} className="mr-2" />
                Location
              </label>
              {errors.location && <small className="field-error">{errors.location}</small>}
              <small className="field-hint">
                Physical address or location description (max 200 characters)
              </small>
            </div>

         
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  Create Warehouse #{nextWarehouseId}
                  <Save className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WareHouses;